import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Mail, X, ArrowLeft, Bookmark } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
);

export default function MagicLogin() {
  const { user, requestMagicLink, startGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/saved" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await requestMagicLink(email.trim());
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper-light flex items-center justify-center px-4 py-10 relative" data-testid="login-page">
      <button type="button" data-testid="auth-back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-ink hover:text-gold">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <Link to="/" data-testid="auth-close" aria-label="Close" className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white border border-[#e5e7eb] hover:border-gold flex items-center justify-center text-ink">
        <X className="w-4 h-4" />
      </Link>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <img src="/seal-oddlytrue-red-wax.png" alt="OddlyTrue" className="w-14 h-14 object-contain seal-stamp" />
          <span className="text-3xl font-extrabold text-ink">Oddly<span className="text-gold">True</span></span>
        </Link>

        <div className="rounded-3xl border border-[#e5e7eb] bg-white shadow-sm p-8">
          {sent ? (
            <div data-testid="magic-sent" className="text-center py-4">
              <span className="mx-auto w-14 h-14 rounded-full bg-paper border border-gold flex items-center justify-center text-ink"><Mail className="w-6 h-6" /></span>
              <h1 className="mt-4 text-2xl font-extrabold text-ink">Check your inbox</h1>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">We sent a sign-in link to <strong className="text-ink">{email}</strong>. It works once and expires in 15 minutes.</p>
              <button type="button" data-testid="magic-resend" onClick={() => setSent(false)} className="mt-6 text-[13px] font-semibold text-gold hover:underline">Use a different email</button>
            </div>
          ) : (
            <>
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold flex items-center gap-1.5"><Bookmark className="w-3.5 h-3.5" /> Save what you love</p>
              <h1 className="mt-2 text-2xl font-extrabold text-ink">Sign in with a magic link</h1>
              <p className="mt-1 text-sm text-ink-muted mb-6">No password. Enter your email and we'll send you a one-tap sign-in link.</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
                  <input data-testid="magic-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-11 pr-4 h-12 rounded-full bg-paper-light border border-[#e5e7eb] text-ink focus:outline-none focus:border-gold focus:bg-white transition-colors" />
                </div>
                {error && <p data-testid="magic-error" className="text-sm text-red-600">{error}</p>}
                <button data-testid="magic-submit" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-gold text-ink font-semibold hover:bg-[#c19614] disabled:opacity-60 transition-colors">
                  {loading ? "Sending link..." : "Email me a sign-in link →"}
                </button>
              </form>
              <div className="flex items-center gap-3 py-5">
                <div className="flex-1 h-px bg-[#e5e7eb]" />
                <span className="text-xs text-ink-muted uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-[#e5e7eb]" />
              </div>
              <button type="button" data-testid="google-signin" onClick={startGoogleLogin} className="w-full flex items-center justify-center gap-3 h-12 rounded-full border border-[#e5e7eb] bg-white text-ink font-semibold hover:border-gold transition-colors">
                <GoogleIcon /> Continue with Google
              </button>
            </>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-ink-muted">By signing in you agree to our <Link to="/privacy" className="underline hover:text-ink">privacy policy</Link>.</p>
      </div>
    </div>
  );
}
